// A realm for code nobody here vouches for: a game someone published. It
// runs in an iframe with `sandbox="allow-scripts"` and no `allow-same-origin`,
// so it has an opaque origin: no cookies, no storage, no reach into this
// page. The frame's CSP allows no network at all. Everything the app needs
// (the wasm core, its bundle, its pak) is fetched here and handed over once;
// after that only two kinds of message cross a private MessageChannel: input
// going in, frame buffers coming out. The app gets no service channel.
//
// This protects the visitor's session and the page. It is not a CPU quota:
// a hostile app can still spin and stall the tab.

import { ANALOG_CENTRE, clampContacts, packStick, type Contact, type HeldInput, type RealmOptions, type RunningRealm, type Surface } from "./realm.ts";

const STEP = 1000 / 60;
/** The most frames one message asks the app to run, so a slow tab does not spiral. */
const MAX_STEPS = 4;
/** How long an app may take to draw its first frame, in milliseconds. */
const BOOT_TIMEOUT_MS = 20_000;
/** What the sandboxed document may do: run the inline runner, and nothing that touches the network. */
const FRAME_CSP = "default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval'; connect-src 'none'; form-action 'none'; base-uri 'none'";

interface Output {
  context: CanvasRenderingContext2D;
  image: ImageData;
}

/** What the runner posts back after it ran some frames. */
interface FrameMessage {
  t: "frame";
  steps: number;
  ready: boolean;
  primary: Uint8ClampedArray;
  secondary: Uint8ClampedArray | null;
}

export class SandboxRealm implements RunningRealm {
  ticks = 0;
  turns = 0;
  failed: Error | null = null;
  private held = 0;
  private analog = ANALOG_CENTRE;
  private right = ANALOG_CENTRE;
  private surface: Surface = "primary";
  private contacts: Contact[] = [];
  private afterTick = new Set<() => void>();
  /** A step message is out and its frame has not come back. */
  private pending = false;
  /** A frame arrived since the last `advance`. */
  private dirty = false;
  private elapsed = 0;
  private outputs: Output[];

  private constructor(private frame: HTMLIFrameElement, private port: MessagePort, private options: RealmOptions) {
    const canvases = [{ canvas: options.primary, size: options.viewport, scale: options.density }];
    if (options.auxiliaryCanvas && options.auxiliary) {
      canvases.push({ canvas: options.auxiliaryCanvas, size: options.auxiliary, scale: 1 });
    }
    this.outputs = canvases.map(({ canvas, size, scale }) => {
      canvas.width = size[0] * scale;
      canvas.height = size[1] * scale;
      return { context: canvas.getContext("2d")!, image: new ImageData(canvas.width, canvas.height) };
    });
  }

  static async boot(options: RealmOptions): Promise<SandboxRealm> {
    // Only this Studio's own files are handed to the frame.
    const get = async (url: string): Promise<Response> => {
      const target = new URL(url, location.href);
      if (target.origin !== location.origin) throw new Error("App assets must come from this Studio.");
      const response = await fetch(target, { credentials: "same-origin" });
      if (!response.ok) throw new Error("App assets are unavailable.");
      return response;
    };
    const [runner, wasm, textWasm, source, pak] = await Promise.all([
      get("/runtime/sandbox.js").then((response) => response.text()),
      get(options.runtimeUrl + "pocketjs.wasm").then((response) => response.arrayBuffer()),
      get(options.runtimeUrl + "pocket_text.wasm").then((response) => response.arrayBuffer()),
      get(options.bundleUrl).then((response) => response.text()),
      get(options.pakUrl).then((response) => response.arrayBuffer()),
    ]);

    const iframe = document.createElement("iframe");
    iframe.hidden = true;
    iframe.setAttribute("sandbox", "allow-scripts");
    iframe.setAttribute("aria-hidden", "true");
    // The runner is inlined: the frame's CSP lets it load nothing by URL.
    iframe.srcdoc =
      `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">` +
      `<script type="module">${runner.replaceAll("</script", "<\\/script")}<\/script>`;

    const channel = new MessageChannel();
    const realm = new SandboxRealm(iframe, channel.port1, options);
    try {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("The app took too long to start.")), BOOT_TIMEOUT_MS);
        channel.port1.onmessage = ({ data }) => {
          if (data?.t === "error") {
            realm.failed = new Error("The app stopped.");
            clearTimeout(timeout);
            reject(realm.failed);
            return;
          }
          if (data?.t !== "frame" || !(data.primary instanceof Uint8ClampedArray)) return;
          if (!realm.paint(data as FrameMessage)) return;
          if (data.ready) {
            clearTimeout(timeout);
            resolve();
          }
        };
        iframe.onload = () => {
          const boot = {
            t: "boot",
            wasm,
            textWasm,
            source,
            pak,
            viewport: options.viewport,
            density: options.density,
            auxiliary: options.auxiliary,
            packageId: options.packageId,
          };
          // The frame has no origin to name, so the target is "*"; the port travels with the message.
          iframe.contentWindow!.postMessage(boot, "*", [channel.port2, wasm, textWasm, pak]);
        };
        document.body.append(iframe);
      });
      return realm;
    } catch (error) {
      realm.dispose();
      throw error;
    }
  }

  /** Put a frame from the app on the canvases. False when it is not the size the device shows. */
  private paint(message: FrameMessage): boolean {
    const pixels = [message.primary, message.secondary];
    for (let i = 0; i < this.outputs.length; i++) {
      const output = this.outputs[i]!;
      const bytes = pixels[i];
      if (!(bytes instanceof Uint8ClampedArray) || bytes.length !== output.image.data.length) {
        this.failed = new Error("Invalid app frame.");
        return false;
      }
      output.image.data.set(bytes);
      output.context.putImageData(output.image, 0, 0);
    }
    this.ticks += Math.min(MAX_STEPS, Math.max(1, message.steps | 0));
    this.pending = false;
    this.dirty = true;
    for (const fn of [...this.afterTick]) {
      this.afterTick.delete(fn);
      fn();
    }
    return true;
  }

  press(bit: number, down: boolean): void {
    this.held = down ? this.held | bit : this.held & ~bit;
  }

  private sizeOf(surface: Surface): [number, number] {
    return surface === "auxiliary" ? this.options.auxiliary ?? this.options.viewport : this.options.viewport;
  }

  touch(surface: Surface, point: [number, number] | null): void {
    this.surface = surface;
    this.contacts = point ? clampContacts([{ id: 0, x: point[0], y: point[1] }], this.sizeOf(surface)) : [];
  }

  /** Everything held from now on: the buttons, both sticks as `frame` takes them, and the contacts on one surface. */
  hold(input: HeldInput): void {
    this.held = input.buttons;
    this.analog = packStick(input.left);
    this.right = packStick(input.right);
    this.surface = input.surface ?? "primary";
    this.contacts = clampContacts(input.contacts, this.sizeOf(this.surface));
  }

  release(): void {
    this.held = 0;
    this.analog = this.right = ANALOG_CENTRE;
    this.contacts = [];
  }

  /** A published app has no account and no Studio service channel. */
  send(_line: object): void {}

  afterNextTick(fn: () => void): void {
    this.afterTick.add(fn);
  }

  /**
   * Ask the app for the frames `dt` covers, one request at a time, and say
   * whether a frame has arrived since the last call.
   */
  advance(dt: number): boolean {
    const dirty = this.dirty;
    this.dirty = false;
    if (this.failed) return dirty;
    this.elapsed = Math.min(250, this.elapsed + dt);
    if (!this.pending && this.elapsed >= STEP) {
      const steps = Math.min(MAX_STEPS, Math.floor(this.elapsed / STEP));
      this.elapsed -= steps * STEP;
      this.pending = true;
      this.turns++;
      this.port.postMessage({
        t: "step", steps, buttons: this.held, analog: this.analog, right: this.right, surface: this.surface, contacts: this.contacts,
      });
    }
    return dirty;
  }

  dispose(): void {
    this.afterTick.clear();
    this.port.close();
    this.frame.remove();
  }
}
